// Commitments registers — both CEO (ceo-monthly-retro.md §5) and Champion
// (champion-monthly-forum.md §5) in one tabbed page.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type CEOCommitment, type ChampionCommitment, type ChampionRow, type RosterRow } from "../lib/api";

const STATUSES = ["open","in_flight","done","dropped"];
const FUNCTIONS = ["Engineering","Sales","HR","Finance","Project Management","Operations","Other"];

const STATUS_COLOR: Record<string, string> = {
  open:      "bg-slate-100 text-slate-800",
  in_flight: "bg-blue-100 text-blue-900",
  done:      "bg-emerald-100 text-emerald-900",
  dropped:   "bg-rose-100 text-rose-900",
};

export function Commitments() {
  const [tab, setTab] = useState<"ceo" | "champion">("ceo");

  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b">
        <button
          className={`px-3 py-1 text-sm ${tab === "ceo" ? "border-b-2 border-blue-600 font-medium" : "text-slate-600"}`}
          onClick={() => setTab("ceo")}
        >CEO commitments</button>
        <button
          className={`px-3 py-1 text-sm ${tab === "champion" ? "border-b-2 border-blue-600 font-medium" : "text-slate-600"}`}
          onClick={() => setTab("champion")}
        >Champion commitments</button>
      </div>
      {tab === "ceo" ? <CEOTab /> : <ChampionTab />}
    </div>
  );
}

function CEOTab() {
  const qc = useQueryClient();
  const { data } = useQuery<CEOCommitment[]>({ queryKey: ["ceo-commitments"], queryFn: () => api.ceoCommitments.list() });
  const roster = useQuery<RosterRow[]>({ queryKey: ["roster"], queryFn: api.roster.list });

  const [form, setForm] = useState({
    retro_date: today(), commitment: "", owner_id: "", due_at: "",
  });
  const create = useMutation({
    mutationFn: () => api.ceoCommitments.create({ ...form, owner_id: form.owner_id || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ceo-commitments"] }); setForm({ retro_date: today(), commitment: "", owner_id: "", due_at: "" }); },
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status, drop_reason }: { id: string; status: string; drop_reason?: string }) =>
      api.ceoCommitments.update(id, { status, drop_reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ceo-commitments"] }),
  });

  return (
    <div className="space-y-4">
      <form className="border rounded p-3 bg-white space-y-2" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <div className="grid grid-cols-4 gap-2">
          <input type="date" className="border rounded px-2 py-1 text-sm" value={form.retro_date}
            onChange={(e) => setForm({ ...form, retro_date: e.target.value })} required />
          <select className="border rounded px-2 py-1 text-sm" value={form.owner_id}
            onChange={(e) => setForm({ ...form, owner_id: e.target.value })}>
            <option value="">— owner —</option>
            {(roster.data ?? []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <input type="date" className="border rounded px-2 py-1 text-sm" placeholder="Due"
            value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} />
          <button className="px-3 py-1 rounded bg-blue-600 text-white text-sm">Add</button>
        </div>
        <input className="w-full border rounded px-2 py-1 text-sm" placeholder="Commitment (one line)"
          value={form.commitment} onChange={(e) => setForm({ ...form, commitment: e.target.value })} required />
      </form>

      <table className="w-full text-sm">
        <thead className="border-b border-slate-300 text-left">
          <tr><th>Retro</th><th>Commitment</th><th>Owner</th><th>Due</th><th>Status</th></tr>
        </thead>
        <tbody>
          {(data ?? []).map((c) => (
            <tr key={c.id} className="border-b border-slate-100">
              <td className="py-2 text-slate-600">{c.retro_date}</td>
              <td>{c.commitment}</td>
              <td className="text-slate-600">{c.owner_name || "—"}</td>
              <td className="text-slate-600">{c.due_at || "—"}</td>
              <td>
                <select
                  className={`text-xs rounded px-1 py-0.5 ${STATUS_COLOR[c.status]}`}
                  value={c.status}
                  onChange={(e) => {
                    const ns = e.target.value;
                    if (ns === "dropped") {
                      const reason = prompt("Drop reason?");
                      if (reason != null) setStatus.mutate({ id: c.id, status: ns, drop_reason: reason });
                    } else {
                      setStatus.mutate({ id: c.id, status: ns });
                    }
                  }}
                >
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChampionTab() {
  const qc = useQueryClient();
  const { data } = useQuery<ChampionCommitment[]>({ queryKey: ["champion-commitments"], queryFn: () => api.championCommitments.list() });
  const champions = useQuery<ChampionRow[]>({ queryKey: ["champions"], queryFn: api.champions.list });

  const [form, setForm] = useState({
    forum_date: today(), champion_id: "", commitment: "", due_at: "",
    beneficiary_functions: [] as string[],
  });

  const create = useMutation({
    mutationFn: () => api.championCommitments.create(form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["champion-commitments"] }); setForm({ forum_date: today(), champion_id: "", commitment: "", due_at: "", beneficiary_functions: [] }); },
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status, drop_reason }: { id: string; status: string; drop_reason?: string }) =>
      api.championCommitments.update(id, { status, drop_reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["champion-commitments"] }),
  });

  const toggleFn = (fn: string) => setForm({ ...form, beneficiary_functions: form.beneficiary_functions.includes(fn) ? form.beneficiary_functions.filter((x) => x !== fn) : [...form.beneficiary_functions, fn] });

  return (
    <div className="space-y-4">
      <form className="border rounded p-3 bg-white space-y-2" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <div className="grid grid-cols-3 gap-2">
          <input type="date" className="border rounded px-2 py-1 text-sm" value={form.forum_date}
            onChange={(e) => setForm({ ...form, forum_date: e.target.value })} required />
          <select className="border rounded px-2 py-1 text-sm" value={form.champion_id}
            onChange={(e) => setForm({ ...form, champion_id: e.target.value })} required>
            <option value="">— champion —</option>
            {(champions.data ?? []).filter((c) => c.champion_id).map((c) =>
              <option key={c.id} value={c.champion_id!}>{c.champion_name} ({c.function})</option>
            )}
          </select>
          <input type="date" className="border rounded px-2 py-1 text-sm" placeholder="Due"
            value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} />
        </div>
        <input className="w-full border rounded px-2 py-1 text-sm" placeholder="Commitment (one line)"
          value={form.commitment} onChange={(e) => setForm({ ...form, commitment: e.target.value })} required />
        <div>
          <div className="text-xs text-slate-600">Beneficiary functions (≥1 required; commitments cross function boundaries)</div>
          <div className="flex flex-wrap gap-2 mt-1">
            {FUNCTIONS.map((f) => (
              <label key={f} className="text-xs">
                <input type="checkbox" className="mr-1" checked={form.beneficiary_functions.includes(f)} onChange={() => toggleFn(f)} />
                {f}
              </label>
            ))}
          </div>
        </div>
        <button className="px-3 py-1 rounded bg-blue-600 text-white text-sm" disabled={form.beneficiary_functions.length === 0}>Add</button>
      </form>

      <table className="w-full text-sm">
        <thead className="border-b border-slate-300 text-left">
          <tr><th>Forum</th><th>Champion</th><th>Commitment</th><th>Beneficiaries</th><th>Due</th><th>Status</th></tr>
        </thead>
        <tbody>
          {(data ?? []).map((c) => (
            <tr key={c.id} className="border-b border-slate-100">
              <td className="py-2 text-slate-600">{c.forum_date}</td>
              <td>{c.champion_name}</td>
              <td>{c.commitment}</td>
              <td className="text-slate-600 text-xs">{c.beneficiary_functions.join(", ")}</td>
              <td className="text-slate-600">{c.due_at || "—"}</td>
              <td>
                <select
                  className={`text-xs rounded px-1 py-0.5 ${STATUS_COLOR[c.status]}`}
                  value={c.status}
                  onChange={(e) => {
                    const ns = e.target.value;
                    if (ns === "dropped") {
                      const reason = prompt("Drop reason?");
                      if (reason != null) setStatus.mutate({ id: c.id, status: ns, drop_reason: reason });
                    } else {
                      setStatus.mutate({ id: c.id, status: ns });
                    }
                  }}
                >
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function today() { return new Date().toISOString().slice(0, 10); }
